import React, { FC } from 'react';

export type ValidationLayerStatus = 'pass' | 'fail' | 'warn' | 'na';

function chipClass(status: ValidationLayerStatus): string {
    switch (status) {
        case 'pass':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'fail':
            return 'bg-red-100 text-red-800 border-red-200';
        case 'warn':
            return 'bg-amber-100 text-amber-900 border-amber-200';
        default:
            return 'bg-slate-100 text-slate-600 border-slate-200';
    }
}

function LayerChip({ label, status }: { label: string; status: ValidationLayerStatus }) {
    const text =
        status === 'pass' ? 'Pass' : status === 'fail' ? 'Fail' : status === 'warn' ? 'Warn' : '—';
    return (
        <span
            className={`inline-flex flex-col rounded border px-2 py-1 text-center text-xs ${chipClass(status)}`}
            title={`${label}: ${text}`}
        >
            <span className="font-semibold uppercase tracking-wide">{label}</span>
            <span>{text}</span>
        </span>
    );
}

type Props = {
    formatSupported?: boolean | null;
    schemaValid?: boolean | null;
    dscsaCompliant?: boolean | null;
    semanticWarningCount?: number;
    schemaWarningCount?: number;
    l2StructuralFallbackUsed?: boolean;
};

const InboundValidationLayersPanel: FC<Props> = ({
    formatSupported,
    schemaValid,
    dscsaCompliant,
    semanticWarningCount = 0,
    schemaWarningCount = 0,
    l2StructuralFallbackUsed = false,
}) => {
    const l1: ValidationLayerStatus =
        formatSupported === true ? 'pass' : formatSupported === false ? 'fail' : 'na';
    let l2: ValidationLayerStatus =
        schemaValid === true ? 'pass' : schemaValid === false ? 'fail' : 'na';
    if (schemaValid === true && (l2StructuralFallbackUsed || schemaWarningCount > 0)) {
        l2 = 'warn';
    }
    const l3: ValidationLayerStatus =
        semanticWarningCount > 0 ? 'warn' : schemaValid != null ? 'pass' : 'na';
    const l4: ValidationLayerStatus =
        dscsaCompliant === true ? 'pass' : dscsaCompliant === false ? 'fail' : 'na';

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                Inbound validation layers
            </p>
            <div className="flex flex-wrap gap-2">
                <LayerChip label="L1 Format" status={l1} />
                <LayerChip label="L2 Schema" status={l2} />
                <LayerChip label="L3 Semantic" status={l3} />
                <LayerChip label="L4 DSCSA" status={l4} />
            </div>
            {l2StructuralFallbackUsed && (
                <p className="mt-2 text-xs text-amber-800">
                    L2 used structural healthcare fallback (strict XSD did not pass). Disable fallback in Integrations
                    for stricter L2.
                </p>
            )}
        </div>
    );
};

export default InboundValidationLayersPanel;
